import { type SelectionEditorActions } from '@/components/tiptap/shared/selection-ai-actions';
import { SelectionAskHeidiBubble } from '@/components/tiptap/shared/selection-ask-heidi-bubble';
import { SelectionHeidiMenu } from '@/components/tiptap/shared/selection-heidi-menu';
import {
  BoldButton,
  ConvertToSectionButton,
  CopyButton,
  FindReplaceButton,
  ItalicButton,
  SnippetButton,
  UnderlineButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { type EditorState } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';

const ICON_SIZE = 14;

export interface SelectionAskHeidiToolbarProps {
  editor: Editor;
  shouldShow: (props: { view: EditorView; state: EditorState; from: number }) => boolean;
  isHeading: boolean;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  emphasisDisabled: boolean;
  emphasisDisabledTooltip?: string;
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onCopy: () => Promise<void>;
  editorActions: SelectionEditorActions;
  onShow?: () => void;
  onHide?: () => void;
}

/**
 * Compact selection toolbar: formatting, copy and editor utilities in one row,
 * then the Ask Heidi dropdown. Link is omitted because the note editor has no
 * hyperlink mark.
 */
export const SelectionAskHeidiToolbar = ({
  editor,
  shouldShow,
  isHeading,
  isBold,
  isItalic,
  isUnderline,
  emphasisDisabled,
  emphasisDisabledTooltip,
  onBold,
  onItalic,
  onUnderline,
  onCopy,
  editorActions,
  onShow,
  onHide,
}: SelectionAskHeidiToolbarProps) => (
  <SelectionAskHeidiBubble
    editor={editor}
    shouldShow={shouldShow}
    onShow={onShow}
    onHide={onHide}
  >
    {!isHeading && (
      <BoldButton
        onClick={onBold}
        active={isBold}
        disabled={emphasisDisabled}
        disabledTooltip={emphasisDisabledTooltip}
        size="md"
        iconSize={ICON_SIZE}
      />
    )}
    <ItalicButton
      onClick={onItalic}
      active={isItalic}
      disabled={emphasisDisabled}
      disabledTooltip={emphasisDisabledTooltip}
      size="md"
      iconSize={ICON_SIZE}
    />
    <UnderlineButton
      onClick={onUnderline}
      active={isUnderline}
      disabled={emphasisDisabled}
      disabledTooltip={emphasisDisabledTooltip}
      size="md"
      iconSize={ICON_SIZE}
    />
    <CopyButton onClick={onCopy} size="md" iconSize={ICON_SIZE} />
    {editorActions.enableFindReplace && (
      <FindReplaceButton
        onClick={editorActions.onFindReplace}
        editor={editor}
        size="md"
        iconSize={ICON_SIZE}
      />
    )}
    {editorActions.enableSnippet && (
      <SnippetButton
        onClick={editorActions.onCreateSnippet}
        editor={editor}
        size="md"
        iconSize={ICON_SIZE}
      />
    )}
    {editorActions.enableConvertToSection && (
      <ConvertToSectionButton
        onClick={editorActions.onConvertToSection}
        size="md"
        iconSize={ICON_SIZE}
      />
    )}
    <SelectionHeidiMenu editor={editor} />
  </SelectionAskHeidiBubble>
);
